"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useAvsInternalAccountsQuery } from "@/lib/queries";
import { cn } from "@/lib/utils";
import { TagsIcon } from "lucide-react";

/** Reference / channel `sellingType` values (display and matching against `AvsInternalAccount.sellingType`). */
export const AVS_SELLING_TYPE_VDL = "VDL" as const;
export const AVS_SELLING_TYPE_VDS = "VDS" as const;
export const AVS_SELLING_TYPE_FLOTTE = "Flotte" as const;

export const SALES_TYPE_VALUES = [
  AVS_SELLING_TYPE_VDL,
  AVS_SELLING_TYPE_VDS,
  AVS_SELLING_TYPE_FLOTTE,
] as const;
export type SalesTypeValue = (typeof SALES_TYPE_VALUES)[number];

function sellingTypeMatchesChannel(
  accountSellingType: string,
  channel: SalesTypeValue,
): boolean {
  return accountSellingType.toLowerCase() === channel.toLowerCase();
}

const OPTIONS: ReadonlyArray<{
  value: SalesTypeValue;
  title: string;
}> = [
  {
    value: AVS_SELLING_TYPE_VDL,
    title: "VDL - Véhicule de location",
  },
  {
    value: AVS_SELLING_TYPE_VDS,
    title: "VDS - Véhicule de service",
  },
  {
    value: AVS_SELLING_TYPE_FLOTTE,
    title: "Flotte",
  },
];

export interface SalesChannelsProps {
  /** Controlled mode: when set, the parent drives this value. */
  value?: SalesTypeValue | "";
  defaultValue?: SalesTypeValue | "";
  onValueChange?: (value: SalesTypeValue) => void;
  /** Sub-select value under the selected channel (controlled mode). */
  subSelectValue?: string;
  defaultSubSelectValue?: string;
  onSubSelectValueChange?: (value: string) => void;
  onCanProceedChange?: (canProceed: boolean) => void;
  /** Prefix for accessible `id`s when multiple instances exist on one page. */
  fieldIdPrefix?: string;
}

export function AvsSalesChannelsStep({
  value: controlledValue,
  defaultValue = "",
  onValueChange,
  subSelectValue: controlledSubValue,
  defaultSubSelectValue = "",
  onSubSelectValueChange,
  onCanProceedChange,
  fieldIdPrefix = "sales-type",
}: SalesChannelsProps) {
  const [uncontrolled, setUncontrolled] = useState<SalesTypeValue | "">(
    defaultValue,
  );
  const [uncontrolledSub, setUncontrolledSub] = useState(defaultSubSelectValue);
  const selected =
    controlledValue !== undefined ? controlledValue : uncontrolled;
  const rawSubSelect =
    controlledSubValue !== undefined ? controlledSubValue : uncontrolledSub;

  const setSelected = useCallback(
    (next: SalesTypeValue) => {
      if (controlledValue === undefined) {
        if (uncontrolled !== next && controlledSubValue === undefined) {
          setUncontrolledSub("");
        }
        setUncontrolled(next);
      }
      onValueChange?.(next);
    },
    [controlledValue, controlledSubValue, uncontrolled, onValueChange],
  );

  const setSubSelected = useCallback(
    (next: string) => {
      if (controlledSubValue === undefined) {
        setUncontrolledSub(next);
      }
      onSubSelectValueChange?.(next);
    },
    [controlledSubValue, onSubSelectValueChange],
  );

  const avsInternalAccountsQuery = useAvsInternalAccountsQuery({
    sort: { label: "asc" },
  });

  const subOptionsBySellingType = useMemo(() => {
    const accounts = avsInternalAccountsQuery.data?.avsInternalAccounts ?? [];
    const map = new Map<
      SalesTypeValue,
      ReadonlyArray<{ value: string; label: string }>
    >();
    for (const channel of SALES_TYPE_VALUES) {
      const subOptions = accounts
        .filter(
          (a) =>
            sellingTypeMatchesChannel(a.sellingType, channel) && a.isActive,
        )
        .map((a) => ({ value: String(a.id), label: a.label }));
      map.set(channel, subOptions);
    }
    return map;
  }, [avsInternalAccountsQuery.data?.avsInternalAccounts]);

  const activeOption = OPTIONS.find((o) => o.value === selected);
  const activeSubOptions = activeOption
    ? (subOptionsBySellingType.get(activeOption.value) ?? [])
    : [];
  const subMatchesChannel =
    !!activeOption && activeSubOptions.some((s) => s.value === rawSubSelect);
  const subForSelect = subMatchesChannel ? rawSubSelect : "";

  const canProceed =
    (selected === AVS_SELLING_TYPE_VDL || selected === AVS_SELLING_TYPE_VDS) &&
    subMatchesChannel;

  useEffect(() => {
    onCanProceedChange?.(canProceed);
  }, [canProceed, onCanProceedChange]);

  return (
    <Card className="border-border">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base font-semibold">
          <TagsIcon className="h-5 w-5 shrink-0 text-[#005EA8]" aria-hidden />
          canal de vente
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 pt-0">
        <div className="space-y-3">
          <Label className="text-sm font-semibold">
            Choix du canal de Vente <span className="text-destructive">*</span>
          </Label>

          <RadioGroup
            value={selected || undefined}
            onValueChange={(v) => setSelected(v as SalesTypeValue)}
            className="gap-3"
          >
            {OPTIONS.map((opt) => {
              const id = `${fieldIdPrefix}-${opt.value.toLowerCase()}`;
              const isChecked = selected === opt.value;
              const optSubOptions =
                subOptionsBySellingType.get(opt.value) ?? [];
              const showSubBlock =
                isChecked &&
                (avsInternalAccountsQuery.isPending ||
                  avsInternalAccountsQuery.isError ||
                  optSubOptions.length > 0);
              return (
                <div
                  key={opt.value}
                  className={cn(
                    "flex cursor-pointer items-center gap-3 rounded-lg border p-4 transition-colors",
                    "hover:bg-muted/40",
                    isChecked
                      ? "border-[#005EA8] bg-[#005EA8]/5 ring-1 ring-[#005EA8]/30"
                      : "border-border",
                  )}
                >
                  <RadioGroupItem
                    value={opt.value}
                    id={id}
                    className="shrink-0 self-center"
                    aria-describedby={`${id}-desc`}
                  />
                  <div className="flex min-w-0 flex-1 flex-col gap-2">
                    <Label
                      htmlFor={id}
                      className="cursor-pointer flex-col items-start gap-1 leading-snug font-normal"
                    >
                      <span className="text-foreground block font-semibold">
                        {opt.title}
                      </span>
                    </Label>
                    {showSubBlock ? (
                      <div
                        className="w-full max-w-md"
                        onPointerDown={(e) => e.stopPropagation()}
                      >
                        {avsInternalAccountsQuery.isPending ? (
                          <Skeleton
                            id={`${id}-sub`}
                            className="h-10 w-full rounded-md"
                            aria-busy
                          />
                        ) : avsInternalAccountsQuery.isError ? (
                          <p
                            id={`${id}-sub`}
                            className="text-destructive text-sm"
                            role="alert"
                          >
                            Impossible de charger les comptes internes.
                          </p>
                        ) : (
                          <Select
                            value={subForSelect || undefined}
                            onValueChange={setSubSelected}
                          >
                            <SelectTrigger id={`${id}-sub`} className="w-full">
                              <SelectValue
                                placeholder={`Sélectionner un compte`}
                              />
                            </SelectTrigger>
                            <SelectContent>
                              {optSubOptions.map((s) => (
                                <SelectItem key={s.value} value={s.value}>
                                  {s.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </RadioGroup>
        </div>
      </CardContent>
    </Card>
  );
}
